import { Component, OnInit, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ListItemRendererComponent } from 'gung-list';
import { first, forkJoin } from 'rxjs';
import { News } from '../../../models/news';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { NewsService } from '../../../services/news/news.service';
import { DateUtilService } from 'gung-common';

@Component({
    selector: 'lib-news-editor-table',
    templateUrl: './news-editor-table.component.html',
    styleUrls: ['./news-editor-table.component.css'],
    standalone: false
})
export class NewsEditorTableComponent extends ListItemRendererComponent<News[]> implements OnInit, AfterViewInit {
  constructor(
    protected gungModalService: GungModalService,
    protected newsService: NewsService,
    protected translationService: TranslateService,
    public dateUtilService: DateUtilService
  ) {
    super();
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.renderFinished?.emit();
  }

  openEditNewsModal(news: News): void {
    this.gungModalService.openEditorNewsModal(news);
  }

  removeNews(news: News): void {
    this.gungModalService
      .openConfirmYesNoModal(undefined, this.translationService.instant('CONFIRM_DELETION', { value: '' }) + '?', {
        size: 'sm'
      })
      .then(
        result => {
          if (result) {
            this.sendDeleteRequest(news);
          }
        },
        reason => {}
      );
  }

  sendDeleteRequest(news: News): void {
    this.newsService.deleteNews(news.id).pipe(first()).subscribe();
  }

  // Pins or unpins straight from the list. A newly pinned news goes last among the pinned.
  togglePin(news: News): void {
    const pinned = !news.extra?.pinned;
    const lastOrder = Math.max(
      0,
      ...this.data.filter(item => item.extra?.pinned).map(item => Number(item.extra?.pinOrder) || 0)
    );
    const extra = { ...news.extra, pinned, ...(pinned ? { pinOrder: lastOrder + 1 } : {}) };
    this.newsService.postNews({ ...news, extra }).pipe(first()).subscribe();
  }

  // Native HTML5 drag and drop, only between pinned rows.
  draggedNews: News | null = null;

  onDragStart(event: DragEvent, news: News): void {
    this.draggedNews = news;
    event.dataTransfer?.setData('text/plain', news.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragOver(event: DragEvent, news: News): void {
    if (this.draggedNews && news.extra?.pinned) {
      event.preventDefault();
    }
  }

  onDrop(event: DragEvent, target: News): void {
    event.preventDefault();
    const dragged = this.draggedNews;
    this.draggedNews = null;
    if (!dragged || dragged.id === target.id || !target.extra?.pinned) {
      return;
    }

    const pinned = this.data.filter(news => news.extra?.pinned);
    const from = pinned.findIndex(news => news.id === dragged.id);
    const to = pinned.findIndex(news => news.id === target.id);
    if (from < 0 || to < 0) {
      return;
    }
    pinned.splice(to, 0, pinned.splice(from, 1)[0]);
    this.savePinOrder(pinned);
  }

  onDragEnd(): void {
    this.draggedNews = null;
  }

  // Renumbers the pinned news 1..n and posts only the ones whose number changed.
  protected savePinOrder(pinned: News[]): void {
    const changed = pinned
      .map((news, index) => ({ news, pinOrder: index + 1 }))
      .filter(({ news, pinOrder }) => Number(news.extra?.pinOrder) !== pinOrder)
      .map(({ news, pinOrder }) => ({ ...news, extra: { ...news.extra, pinOrder } }));

    if (changed.length > 0) {
      forkJoin(changed.map(news => this.newsService.postNews(news))).pipe(first()).subscribe();
    }
  }
}
